import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { User, UserRole } from '../../types'
import { readFunctionError } from '../../lib/functionError'

const ROLE_LABELS: Record<string, string> = {
  admin:          'Admin',
  office_manager: 'Manager',
  instructor:     'Instructor',
  mentor:         'Mentor',
}

interface Props {
  open:           boolean
  staff:          User | null
  /** Caller's role — controls which destination roles are selectable. */
  callerRole:     UserRole | null
  onClose:        () => void
  onSaved:        () => void
}

export default function EditStaffDialog({ open, staff, callerRole, onClose, onSaved }: Props) {
  const [firstName, setFirstName] = useState('')
  const [lastName,  setLastName]  = useState('')
  const [email,     setEmail]     = useState('')
  const [phone,     setPhone]     = useState('')
  const [role,      setRole]      = useState<UserRole>('mentor')
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  useEffect(() => {
    if (!open || !staff) return
    setFirstName(staff.first_name ?? '')
    setLastName(staff.last_name ?? '')
    setEmail(staff.email ?? '')
    setPhone((staff as User & { phone?: string }).phone ?? '')
    setRole(staff.role)
    setError(null)
  }, [open, staff])

  // Office managers cannot promote to admin
  const allowAdminRole = callerRole === 'admin'
  const roleOptions: UserRole[] = allowAdminRole
    ? ['admin', 'office_manager', 'instructor', 'mentor']
    : ['office_manager', 'instructor', 'mentor']

  async function handleSave() {
    if (!staff) return
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('Name and email are required.'); return
    }
    setSaving(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('update-staff', {
      body: {
        user_id:    staff.user_id,
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        email:      email.trim(),
        phone:      phone.trim() || null,
        role,
      },
    })
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
      setSaving(false); return
    }
    if (data?.error) {
      setError(data.error)
      setSaving(false); return
    }
    setSaving(false)
    onSaved()
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit Staff</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name" size="small" fullWidth required
              value={firstName} onChange={e => setFirstName(e.target.value)}
            />
            <TextField
              label="Last name" size="small" fullWidth required
              value={lastName} onChange={e => setLastName(e.target.value)}
            />
          </Box>
          <TextField
            label="Email" size="small" type="email" fullWidth required
            value={email} onChange={e => setEmail(e.target.value)}
            helperText="Email change takes effect immediately."
          />
          <TextField
            label="Phone (optional)" size="small" type="tel" fullWidth
            value={phone} onChange={e => setPhone(e.target.value)}
          />
          <FormControl size="small" fullWidth required>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={role} onChange={e => setRole(e.target.value as UserRole)}>
              {roleOptions.map(r => (
                <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {!allowAdminRole && staff?.role === 'admin' && (
            <Typography variant="caption" color="warning.main">
              You can't edit an admin account. Only an admin can.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
